import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  GetBudgetApprovalsThresholdsResponse,
  UpdateBudgetApprovalThresholdRequest,
  InsertBudgetApprovalThresholdRequest,
  BudgetApprovalAssigneeImportData,
  InsertBudgetApprovalsGroupAssigneesRequest,
  UpdateBudgetApprovalsGroupAssigneesRequest,
  DeleteBudgetApprovalsGroupAssigneesRequest,
} from '@omnea-app/budget-approvals/budgetApprovalsFrontendClient'
import { BudgetApprovalsHttpClient } from '@omnea-app/budget-approvals/budgetApprovalsHttpClient'
import { useAuthenticatedUser } from '@omnea-app/fe-auth/UserSessionProvider'
import { throwIfApiError } from '@omnea-app/packages/universal/error-handling/throwifApiError'
import { isUuid } from '@omnea-app/packages/universal/string-utils/isUuid'

type BudgetApprovalsGroup = {
  id: string
  name: string
}

type InsertBudgetApprovalGroupRequest = {
  name: string
}

type UpdateBudgetApprovalGroupRequest = {
  name: string
}

type DeleteBudgetApprovalGroupRequest = {
  id: string
}

const budgetApprovalsCalculatedAssignees = 'budgetApprovalsCalculatedAssignees'
const budgetApprovalsThresholds = 'budgetApprovalsThresholds'
const budgetApprovalsGroupAssignees = 'budgetApprovalsGroupAssignees'
const budgetApprovalsGroups = 'budgetApprovalsGroups'
const budgetApprovalsGroupCustomCoreData = 'budgetApprovalsGroupCustomCoreData'
const missingDynamicAssigneesCount = 'missingDynamicAssigneesCount'
const dynamicAssigneesWithArchivedScopeCount = 'dynamicAssigneesWithArchivedScopeCount'

export const useGetBudgetApprovalsCalculatedAssignees = (requestId: string) => {
  const { getToken } = useAuthenticatedUser()
  return useQuery({
    queryKey: [budgetApprovalsCalculatedAssignees, requestId],
    enabled: isUuid(requestId),
    queryFn: async () => {
      const authorization = await getToken()
      const budgetApprovalsClient = new BudgetApprovalsHttpClient({ authorization })
      const { data, error } = await budgetApprovalsClient.GET('/get/{requestId}/calculated-assignees', {
        params: { path: { requestId } },
      })
      throwIfApiError(error)
      return data
    },
  })
}

export const useGetBudgetApprovalsGroupAssignees = (groupId: string) => {
  const { getToken } = useAuthenticatedUser()
  return useQuery({
    queryKey: [budgetApprovalsGroupAssignees, groupId],
    queryFn: async () => {
      const authorization = await getToken()
      const budgetApprovalsClient = new BudgetApprovalsHttpClient({ authorization })
      const { data, error } = await budgetApprovalsClient.GET('/get/{requestId}/group-assignees', {
        params: {
          path: { requestId: groupId },
        },
      })
      throwIfApiError(error)
      return data
    },
  })
}

export const useGetBudgetApprovalsThresholds = () => {
  const { getToken } = useAuthenticatedUser()
  return useQuery({
    queryKey: [budgetApprovalsThresholds],
    queryFn: async () => {
      const authorization = await getToken()
      const budgetApprovalsClient = new BudgetApprovalsHttpClient({ authorization })
      const { data, error } = await budgetApprovalsClient.GET('/get/thresholds')
      throwIfApiError(error)
      return data
    },
  })
}

export const useInsertBudgetApprovalsThreshold = () => {
  const queryClient = useQueryClient()
  const { getToken } = useAuthenticatedUser()
  return useMutation({
    mutationFn: async (body: InsertBudgetApprovalThresholdRequest) => {
      const authorization = await getToken()
      const budgetApprovalsClient = new BudgetApprovalsHttpClient({ authorization })
      const { data, error } = await budgetApprovalsClient.POST('/insert/thresholds', {
        body,
      })
      throwIfApiError(error)
      return data
    },
    onSuccess: (successData) => {
      queryClient.setQueryData([budgetApprovalsThresholds], (oldData: GetBudgetApprovalsThresholdsResponse) => [
        ...(oldData ?? []),
        successData,
      ])
    },
  })
}

export const useUpdateBudgetApprovalsThreshold = () => {
  const queryClient = useQueryClient()
  const { getToken } = useAuthenticatedUser()
  return useMutation({
    mutationFn: async ({ id, body }: { id: string; body: UpdateBudgetApprovalThresholdRequest }) => {
      const authorization = await getToken()
      const budgetApprovalsClient = new BudgetApprovalsHttpClient({ authorization })
      const { data, error } = await budgetApprovalsClient.POST('/update/{id}/thresholds', {
        params: { path: { id } },
        body,
      })
      throwIfApiError(error)
      return data
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [budgetApprovalsThresholds] })
    },
  })
}

export const useDeleteBudgetApprovalsThreshold = () => {
  const queryClient = useQueryClient()
  const { getToken } = useAuthenticatedUser()
  return useMutation({
    mutationFn: async (input: { id: string }) => {
      const authorization = await getToken()
      const budgetApprovalsClient = new BudgetApprovalsHttpClient({ authorization })
      const { data, error } = await budgetApprovalsClient.POST('/delete/thresholds', {
        body: { id: input.id },
      })
      throwIfApiError(error)
      return data
    },
    onSuccess: (successData) => {
      queryClient.setQueryData([budgetApprovalsThresholds], (oldData: GetBudgetApprovalsThresholdsResponse) =>
        (oldData ?? []).filter((threshold) => threshold.id !== successData?.id)
      )
    },
  })
}

export const useUploadBudgetApprovalsGroupAssignees = () => {
  const { getToken } = useAuthenticatedUser()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ groupId, body }: { groupId: string; body: BudgetApprovalAssigneeImportData[] }) => {
      const authorization = await getToken()
      const budgetApprovalsClient = new BudgetApprovalsHttpClient({ authorization })

      const { data, error } = await budgetApprovalsClient.POST('/groups/assignees/{groupId}/upload-v2', {
        params: { path: { groupId } },
        body: {
          rows: body,
          dryRun: false,
        },
      })
      throwIfApiError(error)
      return data
    },
    onSuccess: async (_, { groupId }) => {
      for (let i = 1; i <= 3; i++) {
        setTimeout(() => {
          void queryClient.invalidateQueries({ queryKey: [budgetApprovalsGroupAssignees, groupId] })
          void queryClient.invalidateQueries({ queryKey: [missingDynamicAssigneesCount] })
        }, i * 5000)
      }
      await queryClient.invalidateQueries({ queryKey: [missingDynamicAssigneesCount] })
    },
  })
}

export const useGetBudgetApprovalsGroups = () => {
  const { getToken } = useAuthenticatedUser()
  return useQuery({
    queryKey: [budgetApprovalsGroups],
    queryFn: async () => {
      const authorization = await getToken()
      const budgetApprovalsClient = new BudgetApprovalsHttpClient({ authorization })
      const { data, error } = await budgetApprovalsClient.GET('/get/groups')
      throwIfApiError(error)
      return data
    },
  })
}

export const useGetMissingDynamicAssigneesCount = (options?: { skip?: boolean }) => {
  const { getToken } = useAuthenticatedUser()
  return useQuery({
    queryKey: [missingDynamicAssigneesCount],
    enabled: !options?.skip,
    queryFn: async () => {
      const authorization = await getToken()
      const budgetApprovalsClient = new BudgetApprovalsHttpClient({ authorization })
      const { data, error } = await budgetApprovalsClient.GET('/get/missing-dynamic-assignees-count')
      throwIfApiError(error)
      return data
    },
  })
}

export const useGetDynamicAssigneesWithArchivedScopeCount = (options?: { skip?: boolean }) => {
  const { getToken } = useAuthenticatedUser()
  return useQuery({
    queryKey: [dynamicAssigneesWithArchivedScopeCount],
    enabled: !options?.skip,
    queryFn: async () => {
      const authorization = await getToken()
      const budgetApprovalsClient = new BudgetApprovalsHttpClient({ authorization })
      const { data, error } = await budgetApprovalsClient.GET('/get/dynamic-assignees-with-archived-scope-count')
      throwIfApiError(error)
      return data
    },
  })
}

export const useInsertBudgetApprovalsGroup = () => {
  const queryClient = useQueryClient()
  const { getToken } = useAuthenticatedUser()
  return useMutation({
    mutationFn: async (body: InsertBudgetApprovalGroupRequest) => {
      const authorization = await getToken()
      const budgetApprovalsClient = new BudgetApprovalsHttpClient({ authorization })
      const { data, error } = await budgetApprovalsClient.POST('/insert/groups', {
        body,
      })
      throwIfApiError(error)
      return data
    },
    onSuccess: (successData) => {
      queryClient.setQueryData([budgetApprovalsGroups], (oldData: BudgetApprovalsGroup[]) => [...(oldData ?? []), successData])
    },
  })
}

export const useUpdateBudgetApprovalsGroup = () => {
  const queryClient = useQueryClient()
  const { getToken } = useAuthenticatedUser()
  return useMutation({
    mutationFn: async ({ id, body }: { id: string; body: UpdateBudgetApprovalGroupRequest }) => {
      const authorization = await getToken()
      const budgetApprovalsClient = new BudgetApprovalsHttpClient({ authorization })
      const { data, error } = await budgetApprovalsClient.POST('/update/{id}/groups', {
        params: { path: { id } },
        body,
      })
      throwIfApiError(error)
      return data
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [budgetApprovalsGroups] })
    },
  })
}

export const useDeleteBudgetApprovalsGroup = () => {
  const queryClient = useQueryClient()
  const { getToken } = useAuthenticatedUser()
  return useMutation({
    mutationFn: async (body: DeleteBudgetApprovalGroupRequest) => {
      const authorization = await getToken()
      const budgetApprovalsClient = new BudgetApprovalsHttpClient({ authorization })
      const { data, error } = await budgetApprovalsClient.POST('/delete/groups', {
        body,
      })
      throwIfApiError(error)
      return data
    },
    onSuccess: async (_, variables) => {
      queryClient.setQueryData([budgetApprovalsGroups], (oldData: BudgetApprovalsGroup[]) =>
        (oldData ?? []).filter((group) => group.id !== variables.id)
      )
      await queryClient.invalidateQueries({ queryKey: [missingDynamicAssigneesCount] })
    },
  })
}

export const useInsertBudgetApprovalsGroupAssignees = () => {
  const queryClient = useQueryClient()
  const { getToken } = useAuthenticatedUser()
  return useMutation({
    mutationFn: async (body: InsertBudgetApprovalsGroupAssigneesRequest) => {
      const authorization = await getToken()
      const budgetApprovalsClient = new BudgetApprovalsHttpClient({ authorization })
      const { data, error } = await budgetApprovalsClient.POST('/insert/group-assignees', {
        body,
      })
      throwIfApiError(error)
      return data
    },
    onSuccess: async (_, variables) => {
      await queryClient.invalidateQueries({ queryKey: [budgetApprovalsGroupAssignees, variables.budgetApprovalGroupId] })
      await queryClient.invalidateQueries({ queryKey: [missingDynamicAssigneesCount] })
    },
  })
}

export const useUpdateBudgetApprovalsGroupAssignees = () => {
  const queryClient = useQueryClient()
  const { getToken } = useAuthenticatedUser()
  return useMutation({
    mutationFn: async ({ id, body }: { id: string; body: UpdateBudgetApprovalsGroupAssigneesRequest }) => {
      const authorization = await getToken()
      const budgetApprovalsClient = new BudgetApprovalsHttpClient({ authorization })
      const { data, error } = await budgetApprovalsClient.POST('/update/{id}/group-assignees', {
        params: { path: { id } },
        body,
      })
      throwIfApiError(error)
      return data
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [budgetApprovalsGroupAssignees] })
      await queryClient.invalidateQueries({ queryKey: [missingDynamicAssigneesCount] })
    },
  })
}

export const useDeleteBudgetApprovalsGroupAssignees = () => {
  const queryClient = useQueryClient()
  const { getToken } = useAuthenticatedUser()
  return useMutation({
    mutationFn: async (body: DeleteBudgetApprovalsGroupAssigneesRequest) => {
      const authorization = await getToken()
      const budgetApprovalsClient = new BudgetApprovalsHttpClient({ authorization })
      const { data, error } = await budgetApprovalsClient.POST('/delete/group-assignees', {
        body,
      })
      throwIfApiError(error)
      return data
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [budgetApprovalsGroupAssignees] })
      await queryClient.invalidateQueries({ queryKey: [missingDynamicAssigneesCount] })
    },
  })
}

export const useGetBudgetApprovalsGroupCustomCoreData = (groupId: string) => {
  const { getToken } = useAuthenticatedUser()
  return useQuery({
    queryKey: [budgetApprovalsGroupCustomCoreData, groupId],
    queryFn: async () => {
      const authorization = await getToken()
      const budgetApprovalsClient = new BudgetApprovalsHttpClient({ authorization })
      const { data, error } = await budgetApprovalsClient.GET('/get/groups/{groupId}/custom-core-data', {
        params: { path: { groupId } },
      })
      throwIfApiError(error)
      return data
    },
  })
}

export const useInsertBudgetApprovalsGroupCustomCoreData = () => {
  const queryClient = useQueryClient()
  const { getToken } = useAuthenticatedUser()
  return useMutation({
    mutationFn: async ({ groupId, customCoreDataId }: { groupId: string; customCoreDataId: string }) => {
      const authorization = await getToken()
      const budgetApprovalsClient = new BudgetApprovalsHttpClient({ authorization })
      const { data, error } = await budgetApprovalsClient.POST('/insert/group-custom-core-data', {
        body: { groupId, customCoreDataId },
      })
      throwIfApiError(error)
      return data
    },
    onSuccess: async (_, { groupId }) => {
      await queryClient.invalidateQueries({ queryKey: [budgetApprovalsGroupCustomCoreData, groupId] })
    },
  })
}

export const useDeleteBudgetApprovalsGroupCustomCoreData = () => {
  const queryClient = useQueryClient()
  const { getToken } = useAuthenticatedUser()
  return useMutation({
    mutationFn: async ({ groupId, customCoreDataId }: { groupId: string; customCoreDataId: string }) => {
      const authorization = await getToken()
      const budgetApprovalsClient = new BudgetApprovalsHttpClient({ authorization })
      const { data, error } = await budgetApprovalsClient.POST('/delete/group-custom-core-data', {
        body: { groupId, customCoreDataId },
      })
      throwIfApiError(error)
      return data
    },
    onSuccess: async (_, { groupId }) => {
      await queryClient.invalidateQueries({ queryKey: [budgetApprovalsGroupCustomCoreData, groupId] })
    },
  })
}
